import { computed, ref, watch, type Ref } from "vue";
import startCase from "lodash/startCase";
import type { Comment, CommentSettings } from "@/_store/ticket-comments.module";
import { CommentAction, TicketTab, TicketTrigger, TicketType } from "@/constants/tickets";
import { GtmAction, GTMParams } from "@/constants/gtm";
import type { AppContext } from "@/types";

import { trackGtmEvent } from "@/_helpers/analytics";

const SCROLL_CONTAINER_SELECTOR = "#ticket-tab-content";

/**
 * Adapter interface that provides context-specific behavior for the ticket preview composable.
 * This pattern allows different contexts (Single Workspace, MSP, SOC) to use the same
 * composable logic while providing their own store implementations and permission checks.
 *
 * @template TTicket - The ticket type (e.g., Ticket, MspTicket)
 * @template TTicketDetails - The ticket details type (e.g., TicketDetails, MspTicketDetails)
 *
 * @example
 * ```ts
 * const adapter: TicketPreviewAdapter<Ticket, TicketDetails> = {
 *   ticketsStore: {
 *     getTicketDetails: ticketsStore.getTicketDetails,
 *     getTicketActivityLogs: ticketsStore.getTicketActivityLogs,
 *     resetActivityLogs: ticketsStore.resetActivityLogs,
 *     undoActivityLog: ticketsStore.undoActivityLog,
 *   },
 *   commentsStore: {
 *     getComments: commentsStore.getComments,
 *     addComment: commentsStore.addComment,
 *     getNextPage: commentsStore.getNextPage,
 *   },
 *   ticketDetailsLoading,
 *   ticketDetails,
 *   gtmContext: GTMParams.SINGLE_VIEW,
 *   appContext: AppContext.SINGLE_WORKSPACE,
 *   isActivityLogsRestricted: () => isAccessRestricted(...),
 *   isCommentRestricted: () => isWorkspaceFrozenOrActionRestricted(...),
 * };
 * ```
 */
export interface TicketPreviewAdapter<TTicket, TTicketDetails> {
  /** Store methods for ticket operations */
  ticketsStore: {
    /** Fetches ticket details for the given ticket */
    getTicketDetails: (ticket: TTicket) => Promise<void>;
    /** Fetches activity logs for the given event */
    getTicketActivityLogs: (params: { eventId: string; workspaceId?: string }) => Promise<void>;
    /** Resets activity logs pagination state */
    resetActivityLogs: () => void;
    /** Undoes an activity log action */
    undoActivityLog: (id: string) => Promise<void>;
  };

  /** Store methods for comment operations */
  commentsStore: {
    /** Resets comment pagination to initial state */
    resetPagination: () => void;
    /** Fetches comments for the given ticket */
    getComments: (ticket: TTicket) => Promise<void>;
    /** Adds a new comment */
    addComment: (
      payload: CommentSettings & { eventId: string; workspaceId?: string }
    ) => Promise<void>;
    /** Loads next page of comments */
    getNextPage: (params: { eventId: string; workspaceId?: string }) => Promise<void>;
    /** Optional method to perform actions on comments (e.g., mark as read/unread in SOC context) */
    commentAction?: (params: {
      action: CommentAction;
      commentId: string;
      eventId: string;
      workspaceId?: string;
    }) => Promise<void>;
  };

  /** Reactive loading state for ticket details */
  ticketDetailsLoading: Ref<boolean>;
  /** Reactive ticket details data */
  ticketDetails: Ref<TTicketDetails>;
  /** Reactive loading state for activity logs */
  activityLogsLoading: Ref<boolean>;
  /** Reactive comments array */
  comments: Ref<Comment[]>;
  /** Reactive loading state for comments */
  commentsLoading: Ref<boolean>;
  /** Reactive total count of comments */
  totalComments: Ref<number>;

  /** GTM context identifier for analytics (e.g., GTMParams.SINGLE_VIEW, GTMParams.MSP) */
  gtmContext: string;
  /** Application context identifier (e.g., AppContext.SINGLE_WORKSPACE, AppContext.MSP) */
  appContext: AppContext;

  /** Function to check if activity logs access is restricted based on permissions */
  isActivityLogsRestricted: () => boolean;
  /** Function to check if commenting is restricted based on permissions */
  isCommentRestricted: () => boolean;
}

/**
 * Composable that provides shared logic for ticket preview components.
 *
 * This composable handles:
 * - Tab state management (Overview, Details, Activity Logs, Comments)
 * - Ticket data loading and watching for changes
 * - Activity logs management with permission checks
 * - Comments management
 * - GTM analytics tracking
 * - Computed properties for UI state
 *
 * @template TTicket - The ticket type (e.g., Ticket, MspTicket)
 * @template TTicketDetails - The ticket details type with required fields: eventType, eventId, workspaceId, eventTrigger
 *
 * @param ticket - Reactive reference to the current ticket (can be null)
 * @param adapter - Adapter object providing context-specific implementations
 *
 * @returns Object containing:
 * - selectedTab: Current active tab
 * - tabs: Array of all available tabs
 * - showKeyDetails: Whether to show key details section
 * - generalInfoCols: Number of columns for general info grid
 * - activityLogsAccessRestricted: Whether activity logs are restricted
 * - commentNotAllowed: Whether commenting is disabled
 * - hideAllActivityLink: Whether to hide "all activity" link
 * - Store state refs (ticketDetailsLoading, ticketDetails, etc.)
 * - Event handlers (onTabClick, loadMoreActivityLogs, handleAddComment, etc.)
 *
 * @example
 * ```ts
 * const { ticket, isSoc } = toRefs(props);
 * const ticketsStore = useTicketsStore();
 * const commentsStore = useTicketCommentsStore();
 *
 * const adapter: TicketPreviewAdapter<Ticket, TicketDetails> = {
 *   // ... adapter implementation
 * };
 *
 * const {
 *   selectedTab,
 *   tabs,
 *   onTabClick,
 *   handleAddComment,
 *   // ... other returned values
 * } = useTicketPreview(ticket, adapter);
 * ```
 */
export function useTicketPreview<
  TTicket extends {
    eventId: string;
    workspaceId?: string;
    eventTrigger?: TicketTrigger;
    eventType: TicketType;
  },
  TTicketDetails extends {
    eventType: TicketType;
    eventId: string;
    workspaceId?: string;
    eventTrigger?: TicketTrigger;
  },
>(ticket: Ref<TTicket | null>, adapter: TicketPreviewAdapter<TTicket, TTicketDetails>) {
  const selectedTab = ref(TicketTab.OVERVIEW);
  const tabs = ref(Object.values(TicketTab));

  // Check if user has permission to view activity logs
  const activityLogsAccessRestricted = computed(() => adapter.isActivityLogsRestricted());

  // Check if user has permission to add comments
  const commentNotAllowed = computed(() => adapter.isCommentRestricted());

  // Hide "view all activity" link when access is restricted
  const hideAllActivityLink = computed(() => activityLogsAccessRestricted.value);

  // Watch for ticket changes and fetch new data when ticket prop updates
  watch(
    ticket,
    async (newValue, prevValue) => {
      if (!newValue) return;

      const isTicketChange = prevValue && newValue.eventId !== prevValue.eventId;

      // Scroll to top for better UX
      const scrollElement = document.querySelector(SCROLL_CONTAINER_SELECTOR);
      if (scrollElement) {
        scrollElement.scrollTo(0, 0);
      }

      // Reset comment pagination to ensure we start from page 0 for the new ticket
      adapter.commentsStore.resetPagination();

      // Base fetches that always run
      const fetchRequests: Promise<void>[] = [
        adapter.ticketsStore.getTicketDetails(newValue),
        adapter.commentsStore.getComments(newValue),
      ];

      // If we switched tickets while on Activity Logs tab, clear stale logs immediately
      // and batch the activity logs fetch into the same Promise.all — all three requests
      // are independent and can run concurrently.
      if (isTicketChange && selectedTab.value === TicketTab.ACTIVITY_LOGS) {
        adapter.ticketsStore.resetActivityLogs();
        if (!activityLogsAccessRestricted.value) {
          fetchRequests.push(
            adapter.ticketsStore.getTicketActivityLogs({
              eventId: newValue.eventId,
              workspaceId: newValue.workspaceId,
            })
          );
        }
      }

      try {
        await Promise.all(fetchRequests);
      } catch (error) {
        console.error("Error loading ticket data:", error);
      }
    },
    { deep: true, immediate: true }
  );

  /**
   * Handles tab click events. Tracks analytics and loads activity logs when needed.
   */
  const onTabClick = async (tabName: TicketTab) => {
    try {
      // Update the selected tab
      selectedTab.value = tabName;

      const ticketDetails = adapter.ticketDetails.value;
      if (!ticketDetails) return;

      // Track tab navigation in analytics
      const eventTrigger = ticketDetails.eventTrigger || "unknown";
      trackGtmEvent(
        GtmAction.TICKETS,
        `${adapter.gtmContext} - ${GTMParams.TICKET_DETAILS} - ${startCase(eventTrigger)} - ${startCase(tabName)}`
      );

      // Load activity logs when Activity Logs tab is clicked
      if (tabName === TicketTab.ACTIVITY_LOGS) {
        adapter.ticketsStore.resetActivityLogs();
        // Skip loading if user doesn't have permission
        if (activityLogsAccessRestricted.value) return;
        await adapter.ticketsStore.getTicketActivityLogs({
          eventId: ticketDetails.eventId,
          workspaceId: ticketDetails.workspaceId,
        });
      }
    } catch (error) {
      console.error("Error during tab click:", error);
    }
  };

  /**
   * Loads the next page of activity logs for pagination.
   */
  const loadMoreActivityLogs = () => {
    const ticketDetails = adapter.ticketDetails.value;
    adapter.ticketsStore.getTicketActivityLogs({
      eventId: ticketDetails.eventId,
      workspaceId: ticketDetails.workspaceId,
    });
  };

  /**
   * Adds a new comment to the ticket.
   */
  const handleAddComment = async (
    payload: CommentSettings & { eventId: string; workspaceId?: string }
  ) => {
    await adapter.commentsStore.addComment(payload);
  };

  /**
   * Loads the next page of comments for pagination.
   */
  const handleLoadMoreComments = async () => {
    const ticketDetails = adapter.ticketDetails.value;
    if (!ticketDetails) return;

    try {
      await adapter.commentsStore.getNextPage({
        eventId: ticketDetails.eventId,
        workspaceId: ticketDetails.workspaceId,
      });
    } catch (error) {
      console.error("Failed to load more comments:", error);
    }
  };

  /**
   * Undoes an activity log action.
   */
  const undoActivityLog = async (id: string) => {
    await adapter.ticketsStore.undoActivityLog(id);
  };

  return {
    selectedTab,
    tabs,
    activityLogsAccessRestricted,
    commentNotAllowed,
    hideAllActivityLink,
    ticketDetailsLoading: adapter.ticketDetailsLoading,
    ticketDetails: adapter.ticketDetails,
    activityLogsLoading: adapter.activityLogsLoading,
    comments: adapter.comments,
    commentsLoading: adapter.commentsLoading,
    totalComments: adapter.totalComments,
    onTabClick,
    loadMoreActivityLogs,
    handleAddComment,
    handleLoadMoreComments,
    undoActivityLog,
  };
}
